import React, { useState } from 'react';
import { Card, Row, Col, Spinner, Alert, Form, Button } from 'react-bootstrap';
import { SubgroupFilters, usePerformSubgroupAnalysisMutation } from '../../store/api/analysisApi';
import Plot from 'react-plotly.js';
import Slider from 'rc-slider';
import type { Datum } from 'plotly.js';


const SubgroupAnalysis: React.FC = () => {
  const [filters, setFilters] = useState<SubgroupFilters>({
    ageRange: [18, 80],
    bmiRange: [15, 50],
    genders: ['Male', 'Female'],
    smokingStatuses: ['Yes', 'No', 'Former'],
    compareBy: 'Smoking'
  });
  const [analysisData, setAnalysisData] = useState<any>(null);
  const [performSubgroupAnalysis, { isLoading: loading }] = usePerformSubgroupAnalysisMutation();
  const [error, setError] = useState<string | null>(null);

  const boxplotByCategory = analysisData?.plots?.boxplot?.data as Record<string, Datum[]> | undefined;
  const densityPopulation = analysisData?.plots?.density?.population as Datum[] | undefined;
  const densitySubgroup = analysisData?.plots?.density?.subgroup as Datum[] | undefined;

  const densityPopulationX: Datum[] = densityPopulation ?? [];
  const densitySubgroupX: Datum[] = densitySubgroup ?? [];
  const hasPlots =
    Boolean(boxplotByCategory) &&
    Object.keys(boxplotByCategory ?? {}).length > 0 &&
    (densityPopulationX.length > 0 || densitySubgroupX.length > 0);

  const handleApplyFilters = async () => {
    setError(null);
    setAnalysisData(null);
    try {
      const data = await performSubgroupAnalysis(filters).unwrap();
      setAnalysisData(data);
    } catch (err) {
      setError("Failed to get subgroup analysis.");
    }
  };
    
  return (
    <Card>
      <Card.Header as="h4">Advanced Subgroup Filtering</Card.Header>
      <Card.Body>
        <Row className="mb-4">
          <Col md={6}>
            <Form.Group><Form.Label>Age Range: {filters.ageRange.join(' - ')}</Form.Label>
              <Slider
                range min={18} max={80} value={filters.ageRange}
                onChange={(value) => setFilters(f => ({...f, ageRange: value as [number, number]}))}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group><Form.Label>BMI Range: {filters.bmiRange.join(' - ')}</Form.Label>
              <Slider
                range min={15} max={50} step={0.5} value={filters.bmiRange}
                onChange={(value) => setFilters(f => ({...f, bmiRange: value as [number, number]}))}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={4}><Form.Group><Form.Label>Gender</Form.Label>
            {['Male', 'Female'].map(gender => (
              <Form.Check key={gender} type="checkbox" label={gender}
                checked={filters.genders.includes(gender)}
                onChange={e => {
                  const newGenders = e.target.checked
                    ? [...filters.genders, gender]
                    : filters.genders.filter(g => g !== gender);
                  setFilters(f => ({...f, genders: newGenders}));
                }}
              />
            ))}
          </Form.Group></Col>
          <Col md={4}><Form.Group><Form.Label>Smoking Status</Form.Label>
            {['Yes', 'No', 'Former'].map(status => (
              <Form.Check key={status} type="checkbox" label={status}
                checked={filters.smokingStatuses.includes(status)}
                onChange={e => {
                  const newStatuses = e.target.checked
                    ? [...filters.smokingStatuses, status]
                    : filters.smokingStatuses.filter(s => s !== status);
                  setFilters(f => ({...f, smokingStatuses: newStatuses}));
                }}
              />
            ))}
          </Form.Group></Col>
          <Col md={4}><Form.Group><Form.Label>Compare By Category</Form.Label>
            <Form.Select value={filters.compareBy} onChange={e => setFilters(f => ({...f, compareBy: e.target.value}))}>
              <option value="Gender">Gender</option><option value="Smoking">Smoking</option><option value="Location">Location</option>
            </Form.Select>
          </Form.Group></Col>
        </Row>
        <Button onClick={handleApplyFilters} disabled={loading} className="w-100 mt-4">
          {loading ? <Spinner as="span" animation="border" size="sm" /> : 'Apply Filters & Analyze'}
        </Button>

        {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
                
        {analysisData && (
          <div className="mt-4">
            <hr />
            <Row>
              <Col><Card bg="light" className="text-center"><Card.Body><h5>{analysisData.metrics?.filteredCount ?? '-'}</h5><p className="mb-0">Filtered Subjects</p></Card.Body></Card></Col>
              <Col><Card bg="light" className="text-center"><Card.Body><h5>{analysisData.metrics?.meanZScore != null ? Number(analysisData.metrics.meanZScore).toFixed(2) : '-'}</h5><p className="mb-0">Mean Z-Score</p></Card.Body></Card></Col>
            </Row>
            {hasPlots ? (
              <Row className="mt-3">
                <Col md={6}>
                  <Plot
                    data={Object.entries(boxplotByCategory ?? {}).map(([name, values]): any => ({ y: values, type: 'box', name }))}
                    layout={{ title: { text: `Z-Score by ${filters.compareBy}` } }}
                    style={{ width: '100%' }} useResizeHandler={true}
                  />
                </Col>
                <Col md={6}>
                  <Plot
                    data={[
                      { x: densityPopulationX, type: 'histogram', histnorm: 'probability density', name: 'Population', marker: { color: '#cccccc' }, opacity: 0.7 },
                      { x: densitySubgroupX, type: 'histogram', histnorm: 'probability density', name: 'Subgroup', marker: { color: '#0A6353' } },
                    ]}
                    layout={{ title: { text: 'Subgroup vs Population Z-Score' }, barmode: 'overlay' }}
                    style={{ width: '100%' }} useResizeHandler={true}
                  />
                </Col>
              </Row>
            ) : (
              <Alert variant="warning" className="mt-3">
                Not enough data to render subgroup plots.
              </Alert>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};
export default SubgroupAnalysis;